import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink, useSearchParams } from 'react-router-dom-v5-compat'

import AddIcon from '@mui/icons-material/Add'
import CheckIcon from '@mui/icons-material/CheckCircle'
import ClearIcon from '@mui/icons-material/Clear'
import DeleteIcon from '@mui/icons-material/Delete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControl from '@mui/material/FormControl'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import InputLabel from '@mui/material/InputLabel'
import Link from '@mui/material/Link'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import { alpha } from '@mui/material/styles'
import { useDebouncedEffect } from '@react-hookz/web'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
    type Column,
    type OnChangeFn,
    type RowSelectionState,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useSnackbar } from 'notistack'

import { api, getNextPageParam } from '~/api'
import DialogConfirm from '~/components/DialogConfirm'
import FixedBox from '~/components/FixedBox'
import QueryMatcher from '~/components/QueryMatcher'
import CampaignStatus from '~/promotions/components/CampaignStatus'
import { giftWithPurchaseQueryKeys } from '~/promotions/queries/queryKeys'
import { type GiftWithPurchaseCampaign, GiftWithPurchaseCampaignStatus } from '~/promotions/schemas'
import { useAppSelector } from '~/store'
import { formatDate, getPath } from '~/utils'

type GiftWithPurchaseToolbarProps = {
    rowSelection?: RowSelectionState
    onDeleteSelectedCampaigns: () => void
}

function GiftWithPurchaseToolbar({
    rowSelection = {},
    onDeleteSelectedCampaigns,
}: GiftWithPurchaseToolbarProps) {
    const { t } = useTranslation(['common', 'gift_with_purchase'])
    const stores = useAppSelector((state) => state.stores)

    const [searchParams, setSearchParams] = useSearchParams()
    const [nameParam, setNameParam] = useState(() => searchParams.get('search') ?? '')
    useDebouncedEffect(
        () => {
            setSearchParams((prev) => {
                if (nameParam) {
                    prev.set('search', nameParam)
                } else {
                    prev.delete('search')
                }

                return prev
            })
        },
        [nameParam],
        1000
    )

    const selectedRows = useMemo(
        () => Object.keys(rowSelection).map((key) => parseInt(key)),
        [rowSelection]
    )

    const hasSelectedRows = selectedRows.length > 0

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const { enqueueSnackbar } = useSnackbar()
    const { mutate: deleteSelectedCampaigns } = useMutation<number, Error>({
        meta: {
            invalidates: [giftWithPurchaseQueryKeys.lists()],
        },
        mutationFn: async () => {
            const promises = selectedRows.map((id) => {
                return api.promotions.deleteGiftWithPurchaseCampaign.mutation({
                    params: { id },
                })
            })
            const results = await Promise.allSettled(promises)
            const hasError = results.some((result) => result.status === 'rejected')
            if (hasError) {
                throw new Error(t('gift_with_purchase:page_list.actions.delete_campaigns.error'))
            }

            return results.filter((result) => result.status === 'fulfilled').length
        },
        onSuccess: (successAmount) => {
            setDeleteDialogOpen(false)
            enqueueSnackbar(
                t('gift_with_purchase:page_list.actions.delete_campaigns.success', {
                    count: successAmount,
                }),
                { variant: 'success' }
            )
            onDeleteSelectedCampaigns()
        },
        onError: (error) => {
            enqueueSnackbar(error.message, { variant: 'error' })
        },
    })

    return (
        <>
            <Toolbar
                sx={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: (theme) =>
                        hasSelectedRows
                            ? alpha(
                                  theme.palette.secondary.main,
                                  theme.palette.action.selectedOpacity
                              )
                            : undefined,
                }}
            >
                {hasSelectedRows ? (
                    <>
                        <Typography color="secondary" variant="subtitle1">
                            {t('gift_with_purchase:page_list.toolbar.selected', {
                                amount: selectedRows.length,
                            })}
                        </Typography>
                        <Button
                            color="secondary"
                            startIcon={<DeleteIcon />}
                            onClick={() => setDeleteDialogOpen(true)}
                        >
                            {t('gift_with_purchase:page_list.toolbar.button.delete_campaigns', {
                                count: selectedRows.length === 1 ? 0 : selectedRows.length,
                            })}
                        </Button>
                    </>
                ) : (
                    <>
                        <Stack gap={2} direction="row" alignItems="center">
                            <TextField
                                value={nameParam}
                                size="small"
                                label={t(
                                    'gift_with_purchase:page_list.toolbar.search_by_name_input.label'
                                )}
                                placeholder={t(
                                    'gift_with_purchase:page_list.toolbar.search_by_name_input.placeholder'
                                )}
                                InputLabelProps={{ sx: { textTransform: 'capitalize' } }}
                                InputProps={{
                                    endAdornment: searchParams.get('search') ? (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    setSearchParams((prev) => {
                                                        prev.delete('search')
                                                        return prev
                                                    })
                                                }}
                                            >
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null,
                                }}
                                sx={{
                                    width: '100%',
                                    maxWidth: 200,
                                }}
                                onChange={(e) => setNameParam(e.target.value)}
                            />
                            <FormControl>
                                <InputLabel shrink id="status-select-label">
                                    {t('gift_with_purchase:page_list.toolbar.status_select.label')}
                                </InputLabel>
                                <Select
                                    displayEmpty
                                    size="small"
                                    labelId="status-select-label"
                                    label={t(
                                        'gift_with_purchase:page_list.toolbar.status_select.label'
                                    )}
                                    value={searchParams.get('status') ?? ''}
                                    renderValue={(selected) => selected || t('common:all')}
                                    onChange={(e) => {
                                        setSearchParams((prevParams) => {
                                            if (e.target.value) {
                                                prevParams.set('status', e.target.value)
                                            } else {
                                                prevParams.delete('status')
                                            }

                                            return prevParams
                                        })
                                    }}
                                    slotProps={{
                                        input: {
                                            sx: {
                                                width: 150,
                                                textTransform: 'capitalize',
                                            },
                                        },
                                    }}
                                >
                                    <MenuItem sx={{ textTransform: 'capitalize' }} value="">
                                        {t('common:all')}
                                    </MenuItem>
                                    {GiftWithPurchaseCampaignStatus.options
                                        .filter((status) => status !== 'deleted')
                                        .map((status) => (
                                            <MenuItem key={status} value={status}>
                                                {t(
                                                    `gift_with_purchase:gift_with_purchase_campaign_status.${status}`
                                                )}
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        </Stack>
                        {stores.length > 0 && (
                            <Button
                                component={RouterLink}
                                to={getPath('giftWithPurchaseCreate')}
                                variant="outlined"
                                startIcon={<AddIcon />}
                            >
                                {t('gift_with_purchase:page_list.toolbar.button.add_campaign', {
                                    count: 0,
                                })}
                            </Button>
                        )}
                    </>
                )}
            </Toolbar>

            <DialogConfirm
                open={!!deleteDialogOpen}
                title={t('gift_with_purchase:page_list.dialog.delete_campaigns.title', {
                    count: selectedRows.length,
                })}
                message={t('gift_with_purchase:page_list.dialog.delete_campaigns.message', {
                    count: selectedRows.length,
                })}
                confirmText={t('common:delete')}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={deleteSelectedCampaigns}
            />
        </>
    )
}

type GiftWithPurchaseTableProps = {
    rowSelection?: RowSelectionState
    onRowSelectionChange?: OnChangeFn<RowSelectionState>
}

function TableCellSkeleton({ column }: { column: Column<GiftWithPurchaseCampaign> }) {
    return (
        <TableCell
            padding={column.id === 'select' ? 'checkbox' : undefined}
            style={column.columnDef.meta?.style}
        >
            {column.id === 'select' ? (
                <Checkbox disabled />
            ) : column.id === 'applies_to_all_products' ? (
                <Skeleton variant="circular" sx={{ width: 20, height: 20 }} />
            ) : column.id === 'active' ? (
                <Switch disabled />
            ) : (
                <Skeleton variant="text" sx={{ maxWidth: 200 }} />
            )}
        </TableCell>
    )
}

const FETCH_LIMIT = 25

function GiftWithPurchaseTable({ rowSelection, onRowSelectionChange }: GiftWithPurchaseTableProps) {
    const { t } = useTranslation('gift_with_purchase')

    const ownerId = useAppSelector((state) => state.initial.accountOwner.id)
    const [searchParams] = useSearchParams()
    const queryKey = giftWithPurchaseQueryKeys.list({ ownerId, search: searchParams.toString() })

    const queryClient = useQueryClient()

    const query = api.promotions.listGiftWithPurchaseCampaigns.useInfiniteQuery(
        queryKey,
        ({ pageParam = 0 }) => {
            return {
                query: {
                    offset: pageParam * FETCH_LIMIT,
                    status: searchParams.get('status') || undefined,
                    search: searchParams.get('search') || undefined,
                },
                params: {
                    owner_pk: ownerId,
                },
            }
        },
        {
            getNextPageParam: ({ body }) => getNextPageParam(body.next, body.results.length),
        }
    )

    type QueryData = NonNullable<typeof query.data>

    const mutation = api.promotions.updateGiftWithPurchaseCampaign.useMutation({
        meta: {
            invalidates: [giftWithPurchaseQueryKeys.all()],
        },
        onMutate: async ({ body, params }) => {
            const campaignId = params.id
            const isActive = body?.is_active
            if (typeof isActive !== 'boolean') {
                return
            }

            await queryClient.cancelQueries({ queryKey })
            const prevData = queryClient.getQueryData<QueryData>(queryKey)

            await queryClient.setQueryData<QueryData>(queryKey, (data) => {
                if (!data) {
                    return
                }

                return {
                    ...data,
                    pages: data.pages.map((page) => ({
                        ...page,
                        body: {
                            ...page.body,
                            results: page.body.results.map((campaign) => ({
                                ...campaign,
                                is_active:
                                    campaign.id === campaignId ? isActive : campaign.is_active,
                            })),
                        },
                    })),
                }
            })

            return { prevData }
        },
        onError: (_error, _variables, context) => {
            const { prevData } = context as { prevData: QueryData }
            queryClient.setQueryData<QueryData>(queryKey, prevData)
        },
    })

    const updateCampaignActiveState = useCallback(
        (campaignId: number, isActive: boolean) => {
            return mutation.mutate({
                body: {
                    is_active: isActive,
                },
                params: {
                    id: campaignId,
                },
            })
        },
        [mutation.mutate]
    )

    const prefetchCampaign = useCallback(
        (campaignId: number) => {
            return api.promotions.getGiftWithPurchaseCampaign.prefetchQuery(
                queryClient,
                giftWithPurchaseQueryKeys.detail(campaignId),
                {
                    params: { id: campaignId },
                }
            )
        },
        [queryClient]
    )

    const allRows = useMemo(
        () => query.data?.pages.flatMap((page) => page.body.results) ?? [],
        [query.data]
    )

    const table = useReactTable({
        getCoreRowModel: getCoreRowModel(),
        getRowId: (row) => row.id.toString(),
        data: allRows,
        state: {
            rowSelection,
        },
        columns: [
            {
                id: 'select',
                header: ({ table }) => (
                    <Checkbox
                        disabled={!query.isSuccess}
                        color="secondary"
                        checked={table.getIsAllRowsSelected()}
                        indeterminate={table.getIsSomeRowsSelected()}
                        onChange={table.getToggleAllRowsSelectedHandler()}
                    />
                ),
                cell: ({ row }) => (
                    <Checkbox
                        color="secondary"
                        checked={row.getIsSelected()}
                        onChange={row.getToggleSelectedHandler()}
                    />
                ),
                meta: {
                    style: {
                        width: 50,
                    },
                },
            },
            {
                accessorKey: 'name',
                header: t('page_list.table.header.name'),
                cell: ({ getValue, row }) => (
                    <Link
                        color="inherit"
                        underline="hover"
                        component={RouterLink}
                        to={getPath('giftWithPurchaseEdit', {
                            campaign_id: row.original.id.toString(),
                        })}
                        sx={{ p: 2, display: 'block' }}
                        onMouseOver={() => prefetchCampaign(row.original.id)}
                    >
                        {getValue()}
                    </Link>
                ),
                meta: {
                    style: {
                        width: '100%',
                        minWidth: 200,
                    },
                },
            },
            {
                accessorKey: 'status',
                header: () => null,
                cell: ({ getValue }) => (
                    <Box sx={{ textAlign: 'right' }}>
                        <CampaignStatus status={getValue()} />
                    </Box>
                ),
                meta: {
                    style: {
                        width: 120,
                        minWidth: 120,
                    },
                },
            },
            {
                accessorKey: 'start_date',
                header: t('page_list.table.header.start_date'),
                cell: ({ getValue }) => formatDate(getValue(), { format: 'L' }),
                meta: {
                    style: {
                        width: 200,
                        minWidth: 200,
                    },
                },
            },
            {
                accessorKey: 'end_date',
                header: t('page_list.table.header.end_date'),
                cell: ({ getValue }) => formatDate(getValue(), { format: 'L' }),
                meta: {
                    style: {
                        width: 200,
                        minWidth: 200,
                    },
                },
            },
            {
                accessorKey: 'applies_to_all_products',
                header: t('page_list.table.header.applies_to_all_products'),
                cell: ({ getValue }) =>
                    getValue() ? <CheckIcon sx={{ color: 'text.secondary' }} /> : null,
                meta: {
                    style: {
                        width: 80,
                        minWidth: 80,
                    },
                },
            },
            {
                accessorKey: 'is_active',
                header: t('page_list.table.header.active'),
                cell: ({ getValue, row }) => (
                    <Switch
                        color="secondary"
                        tabIndex={-1}
                        checked={getValue()}
                        onChange={(_, checked) => {
                            updateCampaignActiveState(row.original.id, checked)
                        }}
                    />
                ),
                meta: {
                    style: {
                        width: 80,
                        minWidth: 80,
                    },
                },
            },
        ],
        onRowSelectionChange,
    })
    const { rows } = table.getRowModel()

    const tableContainerRef = useRef<HTMLDivElement | null>(null)
    const skeletonRowAmount = 5
    const rowVirtualizer = useVirtualizer({
        estimateSize: () => 70,
        getScrollElement: () => tableContainerRef.current,
        count: query.hasNextPage ? allRows.length + skeletonRowAmount : allRows.length,
    })

    const fetchMoreOnBottomReached = useCallback(
        (containerRefElement?: HTMLDivElement | null) => {
            if (!containerRefElement) {
                return
            }

            const { scrollHeight, scrollTop, clientHeight } = containerRefElement
            const bottomBuffer = 500
            const distanceToBottom = scrollHeight - scrollTop - clientHeight
            const isNearBottom = distanceToBottom < bottomBuffer

            if (query.hasNextPage && isNearBottom && !query.isFetching) {
                query.fetchNextPage()
            }
        },
        [query.hasNextPage, query.isFetching, query.fetchNextPage]
    )

    return (
        <TableContainer
            ref={tableContainerRef}
            sx={{
                overflow: 'auto',
                position: 'relative',
            }}
            onScroll={() => fetchMoreOnBottomReached(tableContainerRef.current)}
        >
            <Table style={{ display: 'grid', width: '100%' }}>
                <TableHead
                    sx={{
                        display: 'grid',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        backgroundColor: (theme) => theme.palette.background.paper,
                    }}
                >
                    {table.getHeaderGroups().map((headerGroup) => (
                        <TableRow key={headerGroup.id} sx={{ height: 'auto' }}>
                            {headerGroup.headers.map((header) => (
                                <TableCell
                                    key={header.id}
                                    colSpan={header.colSpan}
                                    padding={header.column.id === 'select' ? 'checkbox' : undefined}
                                    sx={{
                                        textTransform: 'capitalize',
                                    }}
                                    style={header.column.columnDef.meta?.style}
                                >
                                    {flexRender(
                                        header.column.columnDef.header,
                                        header.getContext()
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableHead>
                <TableBody
                    sx={{
                        display: 'grid',
                        position: 'relative',
                        height: rowVirtualizer.getTotalSize(),
                    }}
                >
                    <QueryMatcher
                        query={query}
                        Loading={Array.from({ length: 10 }).map((_, index) => (
                            <TableRow key={index}>
                                {table.getVisibleLeafColumns().map((column) => (
                                    <TableCellSkeleton key={column.id} column={column} />
                                ))}
                            </TableRow>
                        ))}
                        Errored={(error) => (
                            <TableRow>
                                <TableCell sx={{ display: 'grid', placeItems: 'center' }}>
                                    <Typography color="error" variant="subtitle2" align="center">
                                        {error instanceof Error
                                            ? error.message
                                            : 'Internal server error'}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    >
                        {() => {
                            if (!allRows.length) {
                                return (
                                    <TableRow>
                                        <TableCell sx={{ display: 'grid', placeItems: 'center' }}>
                                            <Typography variant="body2" align="center">
                                                {t('page_list.table.body.no_campaigns')}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )
                            }

                            return rowVirtualizer.getVirtualItems().map((virtualRow) => {
                                const isLoaderRow = virtualRow.index > allRows.length - 1
                                const row = rows[virtualRow.index]

                                if (isLoaderRow) {
                                    return (
                                        <TableRow
                                            key={virtualRow.index + 100}
                                            data-index={virtualRow.index}
                                            sx={{
                                                position: 'absolute',
                                                width: '100%',
                                            }}
                                            style={{
                                                transform: `translateY(${virtualRow.start}px)`,
                                            }}
                                        >
                                            {table.getVisibleLeafColumns().map((column) => (
                                                <TableCellSkeleton
                                                    key={column.id}
                                                    column={column}
                                                />
                                            ))}
                                        </TableRow>
                                    )
                                }

                                return (
                                    <TableRow
                                        hover
                                        key={row.id}
                                        selected={row.getIsSelected()}
                                        data-index={virtualRow.index}
                                        sx={{
                                            position: 'absolute',
                                            width: '100%',

                                            '&.Mui-selected': {
                                                backgroundColor: (theme) =>
                                                    alpha(
                                                        theme.palette.secondary.main,
                                                        theme.palette.action.selectedOpacity
                                                    ),
                                                '&:hover': {
                                                    backgroundColor: (theme) =>
                                                        alpha(
                                                            theme.palette.secondary.main,
                                                            theme.palette.action.selectedOpacity
                                                        ),
                                                },
                                            },
                                        }}
                                        style={{
                                            transform: `translateY(${virtualRow.start}px)`,
                                        }}
                                    >
                                        {row.getVisibleCells().map((cell) => (
                                            <TableCell
                                                key={cell.id}
                                                padding={
                                                    cell.column.id === 'select'
                                                        ? 'checkbox'
                                                        : undefined
                                                }
                                                sx={{
                                                    padding:
                                                        cell.column.id === 'name' ? 0 : undefined,
                                                    textAlign:
                                                        cell.column.id === 'applies_to_all_products'
                                                            ? 'center'
                                                            : undefined,
                                                }}
                                                style={cell.column.columnDef.meta?.style}
                                            >
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext()
                                                )}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                )
                            })
                        }}
                    </QueryMatcher>
                </TableBody>
            </Table>
        </TableContainer>
    )
}

export default function GiftWithPurchase() {
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({})

    const handleDeleteSelectedCampaigns = useCallback(() => {
        setRowSelection({})
    }, [])

    return (
        <FixedBox component={Paper} sx={{ display: 'grid', gridTemplateRows: 'auto 1fr' }}>
            <GiftWithPurchaseToolbar
                rowSelection={rowSelection}
                onDeleteSelectedCampaigns={handleDeleteSelectedCampaigns}
            />
            <GiftWithPurchaseTable
                rowSelection={rowSelection}
                onRowSelectionChange={setRowSelection}
            />
        </FixedBox>
    )
}
